import { useState, useCallback, useRef, useEffect } from "react";

/**
 * useSyncedField - local state that syncs from a server value and saves on blur/Enter.
 *
 * Keeps a local copy for instant UI feedback while the server value is the source of truth.
 * Sync from server happens whenever `serverValue` changes (e.g. from a realtime update via SSE).
 *
 * @example
 * ```tsx
 * const { value, setValue, inputProps } = useSyncedField({
 *   serverValue: group.name,
 *   onSave: (name) => updateGroup({ id: group.id, updates: { name } }),
 * });
 *
 * return <Input {...inputProps} className="h-8" />;
 * ```
 */
export function useSyncedField<T extends string | number>({
  serverValue,
  onSave,
}: {
  serverValue: T;
  onSave: (value: T) => void;
}) {
  const [value, setValue] = useState<T>(serverValue);
  const valueRef = useRef<T>(value);
  const serverValueRef = useRef<T>(serverValue);
  const onSaveRef = useRef(onSave);

  // Keep refs in sync
  valueRef.current = value;
  serverValueRef.current = serverValue;
  onSaveRef.current = onSave;

  // Sync from server
  useEffect(() => {
    setValue(serverValue);
  }, [serverValue]);

  // Stable save function that always reads latest value from ref
  const save = useCallback(() => {
    if (valueRef.current !== serverValueRef.current) {
      onSaveRef.current(valueRef.current);
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        save();
      }
    },
    [save],
  );

  return {
    value,
    setValue,
    save,
    /** Spread onto an <Input> for automatic blur/Enter saving */
    inputProps: {
      value,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value as T;
        setValue(newValue);
        valueRef.current = newValue;
      },
      onBlur: save,
      onKeyDown: handleKeyDown,
    },
  };
}
